import { Box, Tabs, TabsList, TabsTab, Text } from "@salestrak/ui";

import type {
  StepFieldControlProps,
  StepUIDef,
} from "#app/components/automations/canvas/step-ui-registry";
import {
  FieldRow,
  FieldSelect,
  type Option,
} from "#app/components/automations/trigger-sentence";
import {
  filterVariableGroupsByType,
  stepGroupId,
  stepIdFromGroupId,
  TRIGGER_INTEGRATION_GROUP_ID,
  TRIGGER_PROJECT_GROUP_ID,
  useVariableLabel,
} from "#app/components/automations/canvas/automation-variables";
import { JsonVariableEditor } from "#app/components/automations/canvas/json-variable-editor";

// A Source dropdown value encodes a `payloadMode`: "project" / "trigger"
// as-is, an upstream step's output as its variable-group id (stepGroupId). A
// `payloadStepId` with a non-"step" mode is dormant state — the step pick
// remembered across a detour through Custom (tab switches never drop the
// other tab's config) — so it still names the source to return to.
function encodeSource(config: Record<string, unknown>): string | null {
  if (config.payloadMode === "project" || config.payloadMode === "trigger") {
    return config.payloadMode;
  }
  return typeof config.payloadStepId === "string"
    ? stepGroupId(config.payloadStepId)
    : null;
}

// "Call webhook" payload: a Run data / Custom segmented control. Run data
// posts an object from the automation itself, picked in a Source dropdown that
// mirrors what actually exists on this automation (read off the same variable
// groups the insert-variable picker shows):
//   - the triggering project — only when the trigger carries one (an
//     integration-sourced trigger has no project);
//   - the raw trigger event — integration-sourced triggers;
//   - each upstream step that exposes outputs, posting that step's output
//     object.
// Custom is a JSON template editor (seeded with an empty object) that accepts
// variables. Owns three schema fields: `payloadMode` renders here, and the raw
// `payload` + `payloadStepId` are registered "hidden".
function WebhookPayloadControl({
  field,
  config,
  subject,
  variableGroups,
  onConfigChange,
}: StepFieldControlProps) {
  const labelFor = useVariableLabel();
  // Same rule as every other picker: a structured variable is offered only where
  // a field asks for it by TYPE, and this field asks for none. Passing the raw
  // groups here is what would make this the one control still offering a whole
  // project — see `groupsFor` in action-node-fields.tsx. Insertion only: the
  // Source dropdown below enumerates the RAW groups, because posting a whole
  // record is exactly what a run-data source is for.
  const groups = filterVariableGroupsByType(variableGroups, null);

  const editor = (
    <JsonVariableEditor
      value={typeof config.payload === "string" ? config.payload : "{}"}
      onChange={(text) => onConfigChange({ ...config, payload: text })}
      groups={groups}
      labelFor={labelFor}
    />
  );

  // The offered sources, each paired with a sample of the body it posts (the
  // preview under the dropdown). Each row carries the node's unique handle as
  // muted subtext: the step slug from the canvas badge, or `trigger` — the
  // namespace those sources resolve from — for the trigger-sourced rows (only
  // one of which ever appears: a trigger has either a project or an
  // integration context, not both). The project payload's shape is
  // server-assembled (hydrated foreign entities), so its sample rides on the
  // catalog's payloadMode option; the other sources are schema-derived
  // (VariableGroup.example — trigger contextSchema / step outputSchema).
  const sources: Option[] = [];
  const exampleFor = new Map<string, unknown>();
  if (variableGroups.some((g) => g.id === TRIGGER_PROJECT_GROUP_ID)) {
    sources.push({ value: "project", label: subject, description: "trigger" });
    exampleFor.set(
      "project",
      field.options?.find((o) => o.value === "project")?.example,
    );
  }
  for (const g of variableGroups) {
    if (g.id === TRIGGER_INTEGRATION_GROUP_ID) {
      sources.push({
        value: "trigger",
        label: "Trigger event",
        description: "trigger",
      });
      exampleFor.set("trigger", g.example);
    } else if (stepIdFromGroupId(g.id) !== null) {
      // The node's display name, as the picker group and the canvas card show
      // it ("Add note 2", or the author's custom name).
      sources.push({ value: g.id, label: g.label, description: g.slug });
      exampleFor.set(g.id, g.example);
    }
  }

  // With nothing to offer (e.g. a schedule trigger with no upstream steps yet)
  // Run data is an empty dropdown, so the tab is withheld and the JSON editor
  // stands alone.
  if (!sources.length) {
    return <FieldRow label={field.label}>{editor}</FieldRow>;
  }

  // A stored source can stop being offered (the trigger changed, the source
  // step was deleted or re-wired downstream). The dropdown then shows the
  // first source — the same one the runner falls back to when the mode is
  // unset — and the author's next pick writes a real one.
  const stored = encodeSource(config);
  const source =
    stored && sources.some((o) => o.value === stored)
      ? stored
      : (sources[0]?.value ?? null);

  const tab = config.payloadMode === "json" ? "custom" : "data";

  // Switching source or tab rewrites `payloadMode` only — a typed Custom body
  // stays in `payload` (dormant; the runner reads it in "json" mode alone) so
  // no detour through the other tab loses work. `payloadStepId` is cleared
  // only when a NON-step source replaces it: at that point it is stale, and
  // dropping it is what lets a lingering id mean "the step to return to".
  const applySource = (next: string | null) => {
    if (!next) return;
    const stepId = stepIdFromGroupId(next);
    if (stepId !== null) {
      onConfigChange({ ...config, payloadMode: "step", payloadStepId: stepId });
    } else {
      onConfigChange({ ...config, payloadMode: next, payloadStepId: undefined });
    }
  };

  const setTab = (next: string) => {
    if (next === tab) return;
    if (next === "custom") {
      const existing =
        typeof config.payload === "string" && config.payload.trim()
          ? config.payload
          : "{}";
      onConfigChange({ ...config, payloadMode: "json", payload: existing });
    } else {
      // Entering Run data commits the shown source, so the config is concrete
      // without a second click.
      applySource(source);
    }
  };

  // Only while the Run data tab shows it — the Custom tab re-renders per
  // keystroke and must not pay for a pretty-printed body it never renders.
  const previewBody =
    tab === "data" && source ? exampleFor.get(source) : undefined;
  const previewText = previewBody ? JSON.stringify(previewBody, null, 2) : null;

  return (
    <Box direction="column" gap="md">
      <FieldRow label={field.label}>
        <Box gap="sm">
          {/* TabsList is `w-fit` by default; the segmented control spans the
              field row. Each TabsTab grows on its own. */}
          <Tabs onValueChange={(next) => setTab(String(next))} value={tab}>
            <TabsList fullWidth>
              <TabsTab value="data">Run data</TabsTab>
              <TabsTab value="custom">Custom</TabsTab>
            </TabsList>
          </Tabs>
          {tab === "custom" ? (
            <>
              <Text size="sm" variant="muted">
                A custom JSON body. Insert variables to include run data.
              </Text>
              {editor}
            </>
          ) : null}
        </Box>
      </FieldRow>
      {tab === "data" ? (
        <>
          <FieldRow label="Source">
            <FieldSelect
              value={source}
              options={sources}
              placeholder="Select source"
              size="lg"
              ghost
              onSelect={applySource}
            />
          </FieldRow>
          {previewText ? (
            <FieldRow
              info="A sample of the payload's shape — not your actual data. Real values are filled in when the automation runs."
              label="Preview"
            >
              {/* Capped well so a rich body (the project payload) scrolls in
                  place instead of stretching the drawer. */}
              <Box
                border
                borderColor="border"
                maxHeight={280}
                overflow="auto"
                padding="sm"
                rounded="md"
                surface="sunken"
              >
                <Text mono pre size="xs" variant="muted">
                  {previewText}
                </Text>
              </Box>
            </FieldRow>
          ) : null}
        </>
      ) : null}
    </Box>
  );
}

// No label override — the catalog's "Call webhook" flows through.
export const webhookStepUI: StepUIDef = {
  group: "PLUGINS",
  fields: {
    payloadMode: WebhookPayloadControl,
    // Both rendered by WebhookPayloadControl.
    payload: "hidden",
    payloadStepId: "hidden",
  },
  // The card caption is the endpoint alone — the payload fields are a mode
  // key, a node id, and raw JSON text, none of which read on a card.
  summaryFields: ["url"],
};
